/* SIZESOFTX
=========================================================================================
* Descripción: Catálogo de Proveedores
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador             | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1        | 14/05/2026 |                           | Creación catélogo proveedores
*      2        | 14/05/2026 |                           | Validaciones seguridad y campos requeridos
* ---------------------------------------------------------------------------------------
*/

import TextField from "../../ui/TextField";
import { Fragment, useEffect } from "react";
import { Box, Card, CardContent, Divider } from "@mui/material";

import SizeSoftXLayout from "../../../components/leyout/SizeSoftXLayout.jsx";
import MnuProveedoresActions from "./MnuProveedoresActions.jsx";
import SizeSoftXModal from "../../../components/leyout/SizeSoftXModal.jsx";
import ModalBuscarProveedores from "./ModalBuscarProveedores.jsx";
import { useProveedores } from "../../../context/Catalogos/ProveedoresContext";
import { useLayout } from "../../../context/System/LayoutContext";
import { useFormTabLock } from "../../../hooks/useFormTabLock";
import { isErrorForm } from "../../../settings/utils";

export default function FrmProveedores() {
    const { isLocked: isFormLocked } = useFormTabLock('proveedores', {
        title: 'Proveedores',
        onLockDetected: (info) => {
            console.warn(`El formulario de ${info?.title || 'Proveedores'} ya está abierto en otra pestaña`);
        }
    });

    const {
        proveedorPayLoad,
        setProveedorPayLoad,
        statusEditProveedor,
        setStatusEditProveedor,
        statusEnableFields,
        statusOpenModalEliminarProveedor,
        setStatusOpenModalEliminarProveedor,
        statusOpenModalBuscarProveedor,
        handleChangeDataProveedorPayload,
        handleClickNuevoProveedor,
        handleClickEditarProveedor,
        handleClickDeshacer,
        handleClickGuardarProveedor,
        handleClickEliminarProveedor,
        handleClickRegresarMainDashboard,
        handleOpenModalBuscarProveedor,
        handleCloseModalBuscarProveedor,
        handleProveedorSeleccionadoFromModal,
    } = useProveedores();

    const { handleChangeTitle, handleChangeClavePaginaActual, handleOpenAlert } = useLayout();

    useEffect(() => {
        window.scrollTo(0, 0);
        handleChangeTitle("Catálogo de Proveedores");
        handleChangeClavePaginaActual("Proveedores");
    }, []);

    useEffect(() => {
        if (isFormLocked) {
            handleOpenAlert(
                `El formulario de Proveedores ya está abierto en otra pestaña del navegador.\n\nPor favor, cierre la otra ventana o pestaña para continuar.`,
                "error"
            );
            setTimeout(() => {
                handleClickRegresarMainDashboard();
            }, 900);
        }
    }, [isFormLocked]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.altKey && e.key.toLowerCase() === 'f1') {
                e.preventDefault();
                handleOpenModalBuscarProveedor();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleOpenModalBuscarProveedor]);

    const validarCamposRequeridos = () => {
        const camposRequeridos = [
            'razonsocial',
            'nombrecorto',
            'contacto',
            'rfc',
            'direccion',
            'colonia',
            'delegacion',
            'ciudad',
            'cp',
            'telefono1',
            'mail'
        ];

        let tieneErrores = false;
        camposRequeridos.forEach(campo => {
            const valor = proveedorPayLoad[campo]?.value?.trim() || "";
            if (!valor) {
                tieneErrores = true;
                setProveedorPayLoad(prev => ({
                    ...prev,
                    [campo]: { ...prev[campo], error: true, helper: `Campo requerido` }
                }));
            }
        });
        return !tieneErrores;
    };

    const handleGuardarClick = () => {
        if (!validarCamposRequeridos()) {
            return;
        }

        if (isErrorForm(proveedorPayLoad)) {
            return;
        }

        handleClickGuardarProveedor();
    };

    return (
        <Fragment>
            <SizeSoftXLayout
                titlePage="Catálogo de Proveedores"
                buttonActions={<MnuProveedoresActions onGuardar={handleGuardarClick} />}
            >
                <Box>
                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Divider sx={{ mb: 2 }} />

                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                <Box sx={{ flex: '1 1 48%', minWidth: 200 }}>
                                    <TextField autoComplete="off"
                                        name="fecharegistro"
                                        label="Fecha registro"
                                        value={proveedorPayLoad.fecharegistro?.value ?? ""}
                                        disabled
                                        fullWidth
                                        size="small"
                                    />
                                </Box>
                                <Box sx={{ flex: '1 1 48%', minWidth: 200 }}>
                                    <TextField autoComplete="off"
                                        name="claveproveedor"
                                        label="Clave proveedor"
                                        value={proveedorPayLoad.claveproveedor?.value ?? ""}
                                        disabled
                                        fullWidth
                                        size="small"
                                    />
                                </Box>
                                <Box sx={{ flex: '1 1 48%', minWidth: 200 }}>
                                    <TextField autoComplete="off"
                                        name="razonsocial"
                                        label="Razón Social *"
                                        value={proveedorPayLoad.razonsocial?.value ?? ""}
                                        onChange={handleChangeDataProveedorPayload}
                                        disabled={statusEnableFields}
                                        required
                                        fullWidth
                                        size="small"
                                        error={proveedorPayLoad?.razonsocial?.error}
                                        helperText={proveedorPayLoad?.razonsocial?.helper || "Razón social del proveedor"}
                                        inputProps={{ maxLength: 100 }}
                                    />
                                </Box>
                                <Box sx={{ flex: '1 1 48%', minWidth: 200 }}>
                                    <TextField autoComplete="off"
                                        name="nombrecorto"
                                        label="Nombre Corto *"
                                        value={proveedorPayLoad.nombrecorto?.value ?? ""}
                                        onChange={handleChangeDataProveedorPayload}
                                        disabled={statusEnableFields}
                                        required
                                        fullWidth
                                        size="small"
                                        error={proveedorPayLoad?.nombrecorto?.error}
                                        helperText={proveedorPayLoad?.nombrecorto?.helper || "Nombre corto identificador"}
                                        inputProps={{ maxLength: 35 }}
                                    />
                                </Box>
                                <Box sx={{ flex: '1 1 48%', minWidth: 200 }}>
                                    <TextField autoComplete="off"
                                        name="contacto"
                                        label="Contacto *"
                                        value={proveedorPayLoad.contacto?.value ?? ""}
                                        onChange={handleChangeDataProveedorPayload}
                                        disabled={statusEnableFields}
                                        required
                                        fullWidth
                                        size="small"
                                        error={proveedorPayLoad?.contacto?.error}
                                        helperText={proveedorPayLoad?.contacto?.helper || "Nombre del contacto"}
                                        inputProps={{ maxLength: 75 }}
                                    />
                                </Box>
                                <Box sx={{ flex: '1 1 48%', minWidth: 200 }}>
                                    <TextField autoComplete="off"
                                        name="rfc"
                                        label="RFC *"
                                        value={proveedorPayLoad.rfc?.value ?? ""}
                                        onChange={handleChangeDataProveedorPayload}
                                        disabled={statusEnableFields}
                                        required
                                        fullWidth
                                        size="small"
                                        error={proveedorPayLoad?.rfc?.error}
                                        helperText={proveedorPayLoad?.rfc?.helper || "Registro Federal de Contribuyentes"}
                                        inputProps={{ maxLength: 15 }}
                                    />
                                </Box>
                                <Box sx={{ flex: '2 1 65%', minWidth: 200 }}>
                                    <TextField autoComplete="off"
                                        name="direccion"
                                        label="Dirección *"
                                        value={proveedorPayLoad.direccion?.value ?? ""}
                                        onChange={handleChangeDataProveedorPayload}
                                        disabled={statusEnableFields}
                                        required
                                        fullWidth
                                        size="small"
                                        error={proveedorPayLoad?.direccion?.error}
                                        helperText={proveedorPayLoad?.direccion?.helper || "Dirección completa"}
                                        inputProps={{ maxLength: 100 }}
                                    />
                                </Box>
                                <Box sx={{ flex: '1 1 48%', minWidth: 200 }}>
                                    <TextField autoComplete="off"
                                        name="colonia"
                                        label="Colonia/Comunidad *"
                                        value={proveedorPayLoad.colonia?.value ?? ""}
                                        onChange={handleChangeDataProveedorPayload}
                                        disabled={statusEnableFields}
                                        required
                                        fullWidth
                                        size="small"
                                        error={proveedorPayLoad?.colonia?.error}
                                        helperText={proveedorPayLoad?.colonia?.helper || "Colonia o comunidad"}
                                        inputProps={{ maxLength: 50 }}
                                    />
                                </Box>
                                <Box sx={{ flex: '1 1 48%', minWidth: 200 }}>
                                    <TextField autoComplete="off"
                                        name="delegacion"
                                        label="Delegación/Municipio *"
                                        value={proveedorPayLoad.delegacion?.value ?? ""}
                                        onChange={handleChangeDataProveedorPayload}
                                        disabled={statusEnableFields}
                                        required
                                        fullWidth
                                        size="small"
                                        error={proveedorPayLoad?.delegacion?.error}
                                        helperText={proveedorPayLoad?.delegacion?.helper || "Delegación o municipio"}
                                        inputProps={{ maxLength: 50 }}
                                    />
                                </Box>
                                <Box sx={{ flex: '1 1 32%', minWidth: 150 }}>
                                    <TextField autoComplete="off"
                                        name="ciudad"
                                        label="Ciudad/Población *"
                                        value={proveedorPayLoad.ciudad?.value ?? ""}
                                        onChange={handleChangeDataProveedorPayload}
                                        disabled={statusEnableFields}
                                        required
                                        fullWidth
                                        size="small"
                                        error={proveedorPayLoad?.ciudad?.error}
                                        helperText={proveedorPayLoad?.ciudad?.helper || "Ciudad o población"}
                                        inputProps={{ maxLength: 50 }}
                                    />
                                </Box>
                                <Box sx={{ flex: '0 1 32%', minWidth: 150 }}>
                                    <TextField autoComplete="off"
                                        name="cp"
                                        label="Código Postal *"
                                        value={proveedorPayLoad.cp?.value ?? ""}
                                        onChange={handleChangeDataProveedorPayload}
                                        disabled={statusEnableFields}
                                        required
                                        fullWidth
                                        size="small"
                                        error={proveedorPayLoad?.cp?.error}
                                        helperText={proveedorPayLoad?.cp?.helper || "Código postal"}
                                        inputProps={{ maxLength: 6 }}
                                    />
                                </Box>
                                <Box sx={{ flex: '0 1 32%', minWidth: 150 }}>
                                    <TextField autoComplete="off"
                                        name="telefono1"
                                        label="Teléfono 1 *"
                                        value={proveedorPayLoad.telefono1?.value ?? ""}
                                        onChange={handleChangeDataProveedorPayload}
                                        disabled={statusEnableFields}
                                        required
                                        fullWidth
                                        size="small"
                                        error={proveedorPayLoad?.telefono1?.error}
                                        helperText={proveedorPayLoad?.telefono1?.helper || "Teléfono principal"}
                                        inputProps={{ maxLength: 15 }}
                                    />
                                </Box>
                                <Box sx={{ flex: '0 1 15%', minWidth: 80 }}>
                                    <TextField autoComplete="off"
                                        name="extencion1"
                                        label="Ext.1"
                                        value={proveedorPayLoad.extencion1?.value ?? ""}
                                        onChange={handleChangeDataProveedorPayload}
                                        disabled={statusEnableFields}
                                        fullWidth
                                        size="small"
                                        error={proveedorPayLoad?.extencion1?.error}
                                        helperText={proveedorPayLoad?.extencion1?.helper || "Extensión telefónica"}
                                        inputProps={{ maxLength: 8 }}
                                    />
                                </Box>
                                <Box sx={{ flex: '0 1 32%', minWidth: 150 }}>
                                    <TextField autoComplete="off"
                                        name="telefono2"
                                        label="Teléfono 2"
                                        value={proveedorPayLoad.telefono2?.value ?? ""}
                                        onChange={handleChangeDataProveedorPayload}
                                        disabled={statusEnableFields}
                                        fullWidth
                                        size="small"
                                        error={proveedorPayLoad?.telefono2?.error}
                                        helperText={proveedorPayLoad?.telefono2?.helper || "Teléfono secundario"}
                                        inputProps={{ maxLength: 15 }}
                                    />
                                </Box>
                                <Box sx={{ flex: '0 1 15%', minWidth: 80 }}>
                                    <TextField autoComplete="off"
                                        name="extencion2"
                                        label="Ext.2"
                                        value={proveedorPayLoad.extencion2?.value ?? ""}
                                        onChange={handleChangeDataProveedorPayload}
                                        disabled={statusEnableFields}
                                        fullWidth
                                        size="small"
                                        error={proveedorPayLoad?.extencion2?.error}
                                        helperText={proveedorPayLoad?.extencion2?.helper || "Extensión telefónica"}
                                        inputProps={{ maxLength: 8 }}
                                    />
                                </Box>
                                <Box sx={{ flex: '1 1 32%', minWidth: 150 }}>
                                    <TextField autoComplete="off"
                                        name="mail"
                                        label="Email *"
                                        value={proveedorPayLoad.mail?.value ?? ""}
                                        onChange={handleChangeDataProveedorPayload}
                                        disabled={statusEnableFields}
                                        required
                                        fullWidth
                                        size="small"
                                        error={proveedorPayLoad?.mail?.error}
                                        helperText={proveedorPayLoad?.mail?.helper || "Correo electrónico"}
                                        inputProps={{ maxLength: 100 }}
                                    />
                                </Box>
                                <Box sx={{ flex: '2 1 48%', minWidth: 200 }}>
                                    <TextField autoComplete="off"
                                        name="condicionespago"
                                        label="Condiciones de Pago"
                                        value={proveedorPayLoad.condicionespago?.value ?? ""}
                                        onChange={handleChangeDataProveedorPayload}
                                        disabled={statusEnableFields}
                                        fullWidth
                                        size="small"
                                        error={proveedorPayLoad?.condicionespago?.error}
                                        helperText={proveedorPayLoad?.condicionespago?.helper || "Términos de pago (opcional)"}
                                        multiline
                                        rows={2}
                                        inputProps={{ maxLength: 500 }}
                                    />
                                </Box>
                                <Box sx={{ flex: '2 1 48%', minWidth: 200 }}>
                                    <TextField autoComplete="off"
                                        name="acuerdos"
                                        label="Acuerdos"
                                        value={proveedorPayLoad.acuerdos?.value ?? ""}
                                        onChange={handleChangeDataProveedorPayload}
                                        disabled={statusEnableFields}
                                        fullWidth
                                        size="small"
                                        error={proveedorPayLoad?.acuerdos?.error}
                                        helperText={proveedorPayLoad?.acuerdos?.helper || "Acuerdos comerciales (opcional)"}
                                        multiline
                                        rows={2}
                                        inputProps={{ maxLength: 500 }}
                                    />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </SizeSoftXLayout>

            <SizeSoftXModal
                open={statusOpenModalEliminarProveedor}
                title={"¿Está seguro de querer eliminar el proveedor? Esta acción no se puede deshacer."}
                onClose={() => setStatusOpenModalEliminarProveedor(false)}
                onSuccess={handleClickEliminarProveedor}
                success="Eliminar"
            />

            <ModalBuscarProveedores
                openModal={statusOpenModalBuscarProveedor}
                handleCloseModal={handleCloseModalBuscarProveedor}
                onSelectProveedor={handleProveedorSeleccionadoFromModal}
            />
        </Fragment>
    );
}